/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.localization = null;
      this.links = [];
      this.menu = null;

      this.isPaused = false;

      // slider timer references for cleanup
      this._nextSlideTimeout = null;

      // Bind once (so add/remove works)
      this._onMouseEnter = this.pauseSlider.bind(this);
      this._onMouseLeave = this.resumeSlider.bind(this);

      // Delay init until markup exists (Shopify can inject/replace)
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.removeEventListener('mouseenter', this._onMouseEnter);
        this.slider.removeEventListener('mouseleave', this._onMouseLeave);
      }

      if (this._markupObserver) {
        this._markupObserver.disconnect();
        this._markupObserver = null;
      }

      if (this._nextSlideTimeout) {
        clearTimeout(this._nextSlideTimeout);
        this._nextSlideTimeout = null;
      }
    }

    cacheDom() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = Array.from(this.querySelectorAll('.js-announcement-link'));
      this.menu = document.querySelector('.main-menu__content');
    }

    bindEvents() {
      // breakpoint-change handlers
      if (!this.moveLinksHandler) {
        this.moveLinksHandler = this.moveLinks.bind(this);
      }
      if (!this.moveLocalizationHandler) {
        this.moveLocalizationHandler = debounce(this.moveLocalization.bind(this));
      }

      window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);

      // slider hover pause
      if (this.slider) {
        this.slider.removeEventListener('mouseenter', this._onMouseEnter);
        this.slider.removeEventListener('mouseleave', this._onMouseLeave);
        this.slider.addEventListener('mouseenter', this._onMouseEnter);
        this.slider.addEventListener('mouseleave', this._onMouseLeave);
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      this.cacheDom();

      // If your markup/menu isn't available yet, wait for it safely
      if (!this.slider && !this.localization && (!this.links || this.links.length === 0)) {
        this.waitForMarkupAndInit();
        return;
      }

      this.bindEvents();

      // Start slider only when we actually have slides
      if (this.slider) {
        const slides = Array.from(this.slider.querySelectorAll('.announcement__text'));

        // ✅ Fix for your error: slides[0] can be undefined
        if (slides.length > 0) {
          // Reset any existing visible states (optional safety)
          slides.forEach((s) => s.classList.remove('is-visible'));
          slides[0].classList.add('is-visible');

          const delay = this.getSlideDelay();

          const nextSlide = () => {
            // reschedule first (so even if something errors, it doesn't die silently)
            this._nextSlideTimeout = setTimeout(nextSlide, delay);

            if (this.isPaused) return;
            if (!this.slider) return;

            // theme.elementUtil might not exist yet in some loads
            const inViewport =
              window.theme?.elementUtil?.isInViewport
                ? window.theme.elementUtil.isInViewport(this)
                : true;

            if (!inViewport) return;

            const current = this.slider.querySelector('.announcement__text.is-visible');
            if (!current) {
              // fallback: ensure first is visible
              slides[0]?.classList.add('is-visible');
              return;
            }

            const currIndex = slides.indexOf(current);
            const nextIndex = currIndex + 1 < slides.length ? currIndex + 1 : 0;

            const nextEl = slides[nextIndex];
            if (!nextEl) return;

            // Scroll behavior
            if (document.dir === 'rtl') {
              this.slider.scrollTo(-Math.abs(nextIndex * nextEl.clientWidth), 0);
            } else {
              // keep your -32 offset
              this.slider.scrollTo(nextEl.offsetLeft - 32, 0);
            }

            nextEl.classList.add('is-visible');
            current.classList.remove('is-visible');
          };

          // clear any existing timer then start
          if (this._nextSlideTimeout) clearTimeout(this._nextSlideTimeout);
          this._nextSlideTimeout = setTimeout(nextSlide, delay);
        }
      }

      // Move links/localization once on init (guarded)
      if (this.links && this.links.length) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Wait for inner markup to exist (common when Shopify sections are re-rendered)
     */
    waitForMarkupAndInit() {
      if (this._markupObserver) this._markupObserver.disconnect();

      this._markupObserver = new MutationObserver(() => {
        this.cacheDom();

        // If any of the important parts appear, init again
        if (this.slider || this.localization || (this.links && this.links.length)) {
          this._markupObserver.disconnect();
          this._markupObserver = null;
          // Run init on next frame so layout is stable
          requestAnimationFrame(() => this.init());
        }
      });

      this._markupObserver.observe(this, { childList: true, subtree: true });
    }

    getSlideDelay() {
      const raw = this.dataset.slideDelay;
      const n = parseInt(raw, 10);
      // fallback to 4000ms if missing/invalid
      return Number.isFinite(n) && n > 0 ? n : 4000;
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      // If menu isn't in DOM (or theme not ready), safely skip
      this.menu = document.querySelector('.main-menu__content');
      if (!this.menu) return;

      // refresh links (they can change when section re-renders)
      this.links = Array.from(this.querySelectorAll('.js-announcement-link'));
      if (!this.links.length) {
        // If we previously created the mobile container, remove it
        const existing = document.querySelector('.mob__announcement-links');
        if (existing) existing.remove();
        return;
      }

      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');

      // theme.mediaMatches might not exist early
      const isMd = window.theme?.mediaMatches?.md ?? true;

      if (!isMd && !menuAnnouncementLinks) {
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNavUl.classList.add('secondary-nav');

        this.links.forEach((link) => {
          // build LI correctly
          const li = document.createElement('li');
          li.innerHTML = `<a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
          mobNavUl.appendChild(li);
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (isMd && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      // Re-cache in case of section refresh
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.menu = document.querySelector('.main-menu__content');

      if (!this.menu) return;

      const menuLocalization = document.querySelector('.mob__localization');
      const isMd = window.theme?.mediaMatches?.md ?? true;

      if (!this.localization) {
        // If localization doesn't exist, remove mobile container if it exists
        if (menuLocalization) menuLocalization.remove();
        return;
      }

      if (!isMd && !menuLocalization) {
        // Move localization to mobile
        const firstChild = this.localization.firstElementChild;
        if (!firstChild) return;

        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(firstChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (isMd && menuLocalization) {
        // Move localization back to announcement bar
        const child = menuLocalization.firstElementChild;
        if (!child) {
          menuLocalization.remove();
          return;
        }
        this.localization.appendChild(child);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}